import { Fragment } from "react";
import { MapPin, Banknote, CalendarClock, BadgePercent, Building2 } from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { fmtPrice, BADGE } from "../../assets/data";
import FavoriteButton from "../FavoriteButton";
import CompareButton from "../CompareButton";

export default function ProjectHero({ p }) {
  const { lang, cur } = useAppContext();

  const stats = [
    {
      key: "price",
      icon: <Banknote size={16} />,
      label: lang === "en" ? "Starting Price" : "يبدأ من",
      value: fmtPrice(p, cur),
    },
    {
      key: "inst",
      icon: <CalendarClock size={16} />,
      label: lang === "en" ? "Installment" : "تقسيط",
      value: p.installmentDuration ? `${p.installmentDuration} Months` : "—",
    },
    {
      key: "dp",
      icon: <BadgePercent size={16} />,
      label: lang === "en" ? "Downpayment" : "المقدم",
      value: p.downPayment
        ? fmtPrice({ priceEGP: p.downPayment, priceSAR: null, priceAED: null }, "EGP")
        : "—",
    },
    {
      key: "delivery",
      icon: <Building2 size={16} />,
      label: lang === "en" ? "Delivery" : "التسليم",
      value: p.deliveryYear || "—",
    },
  ];

  const badge = p.badge ? BADGE[p.badge] : null;

  return (
    <div className="pd-hero">
      {/* Image + overlay (clipped to hero bounds) */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        <img
          src={
            p.photos?.[0] ||
            p.img ||
            "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=80"
          }
          alt={lang === "ar" ? p.nameAr : p.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(10,22,40,.95) 0%, rgba(10,22,40,.5) 45%, rgba(10,22,40,.12) 100%)",
          }}
        />
      </div>

      {/* Badge */}
      {badge && (
        <div
          style={{
            position: "absolute",
            top: "2rem",
            left: "5%",
            zIndex: 2,
            background: badge.bg,
            color: badge.color,
            border: `1px solid ${badge.border}`,
            borderRadius: 4,
            padding: "5px 14px",
            fontSize: ".68rem",
            fontFamily: "var(--sans)",
            letterSpacing: ".13em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {p.badge}
        </div>
      )}

      {/* Status pill */}
      <div
        style={{
          position: "absolute",
          top: "2rem",
          right: "5%",
          zIndex: 2,
          background: p.status === "Ready to Move" ? "rgba(34,197,94,.15)" : "rgba(234,179,8,.12)",
          border: `1px solid ${p.status === "Ready to Move" ? "rgba(34,197,94,.4)" : "rgba(234,179,8,.35)"}`,
          color: p.status === "Ready to Move" ? "#4ade80" : "#fbbf24",
          borderRadius: 100,
          padding: "5px 14px",
          fontSize: ".7rem",
          fontFamily: "var(--sans)",
          letterSpacing: ".08em",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "currentColor",
            flexShrink: 0,
          }}
        />
        {p.status}
      </div>

      {/* Favorite + Compare action buttons */}
      <div
        style={{
          position: "absolute",
          top: "5rem",
          right: "5%",
          zIndex: 2,
          display: "flex",
          gap: ".5rem",
        }}
      >
        <div style={{ position: "relative", width: 38, height: 38 }}>
          <FavoriteButton propertyId={p.id} style={{ position: "relative", top: "auto", right: "auto", left: "auto", bottom: "auto", width: 38, height: 38 }} />
        </div>
        <div style={{ position: "relative", width: 38, height: 38 }}>
          <CompareButton propertyId={p.id} style={{ position: "relative", top: "auto", right: "auto", left: "auto", bottom: "auto", width: 38, height: 38 }} />
        </div>
      </div>

      {/* Hero text content */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "3rem 5% 7.5rem",
          zIndex: 2,
        }}
      >
        {p.developerName && (
          <div
            className="fadeUp"
            style={{
              fontSize: ".6rem",
              color: "rgba(255,255,255,.45)",
              letterSpacing: ".28em",
              textTransform: "uppercase",
              marginBottom: ".6rem",
              fontFamily: "var(--sans)",
            }}
          >
            {p.developerName}
          </div>
        )}
        <h1
          className="fadeUp d1"
          style={{
            fontFamily: "var(--serif)",
            fontSize: "clamp(2.2rem,6vw,4.5rem)",
            color: "#fff",
            fontWeight: 300,
            lineHeight: 1.05,
            marginBottom: ".75rem",
          }}
        >
          {lang === "ar" ? p.nameAr : p.name}
        </h1>
        <div className="fadeUp d2" style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <MapPin size={13} color="var(--gold)" />
          <span
            style={{
              color: "rgba(255,255,255,.58)",
              fontSize: ".85rem",
              fontFamily: "var(--sans)",
            }}
          >
            {lang === "ar" ? p.locationAr || p.location : p.location}
          </span>
        </div>
      </div>

      {/* Floating glass-morphism stats bar */}
      <div className="pd-stats-bar fadeUp d3">
        {stats.map((stat, i) => (
          <Fragment key={stat.key}>
            {i > 0 && <div className="pd-stat-divider" />}
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: "var(--gold)",
                  marginBottom: ".4rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {stat.icon}
              </div>
              <div
                style={{
                  fontSize: ".57rem",
                  color: "rgba(255,255,255,.4)",
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  fontFamily: "var(--sans)",
                  marginBottom: ".3rem",
                }}
              >
                {stat.label}
              </div>
              <div
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: "1.05rem",
                  color: "#fff",
                  fontWeight: 400,
                }}
              >
                {stat.value}
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
